import React, {useEffect, useRef, useState} from "react";
import {observer} from "mobx-react-lite";
import intl from "react-intl-universal";
import {useRootStore, useUserState} from "@stores/AppStateContext";

import {PreventRerender} from "../../../components/PreventRerender";

import {JumpToElementIcon} from "../../../components/icons/JumpToElementIcon";
import {SideModal} from "../components/SideModal/SideModal";
import {showInWorkspace} from "../../../../actions/ui/showInWorkspace";
import {Button} from "../../../components/Button";
import {ImageGalleryPreview} from "../../../components/ImageGalleryPreview";
import {getDocumentLocalFirst} from "../../../../stores/document/DocumentLocal";

import {PAGES} from "../../../../stores/ViewStore";

import {DocumentViewModalProps} from "../modal-names";

import './DocumentViewModal.css';

export const DocumentViewModal = observer((
    {
        task,
        calendarTable,
    }: DocumentViewModalProps
) => {
    const rootStore = useRootStore();
    const userState = useUserState();
    const editorNodeRef = useRef<HTMLDivElement | null>(null);
    const editorRef = useRef<any | null>(null);
    const [content, setContent] = useState<any | null>(null);
    const documentId = task.relatedPage;
    const taskListUnit = calendarTable.taskListUnitDS.getTaskListUnitById(task.taskListUnitId);
    const taskListName = Object.values(taskListUnit?.taskLists ?? {}).find(list => list.id === task?.taskListId)?.name ?? intl.get('taskList.ungrouped.short').d('Ungrouped Tasks');

    useEffect(() => {
        if (documentId) {
            const abortController = new AbortController();
            getDocumentLocalFirst(userState!.wsServerConnection, documentId, abortController)
                .then(res => {
                    if (!abortController.signal.aborted) {
                        setContent(res.doc);
                    }
                })
                .catch(err => console.error(err));
            return () => abortController.abort();
        }
        return;
    }, [documentId]);

    useEffect(() => {
        if (content && editorNodeRef.current) {
            let abortController = new AbortController();
            import("../../../../Editor/CollabEditor")
                .then(mod => {
                    if (abortController.signal.aborted) return;
                    const ViewOnlyEditor = mod.ViewOnlyEditor;
                    editorRef.current = new ViewOnlyEditor(editorNodeRef.current!, content);
                })
                .catch(err => console.error(err));
            return () => {
                abortController.abort();
                editorRef.current?.close();
            };
        }
        return;
    }, [content]);

    const item = documentId && rootStore.view.workspace?.items.get(documentId);
    const itemHeader = item ? (item.name || item.getNamePlaceholder(rootStore.currentLocale)) : undefined;

    return (
        <SideModal
            header={task.name}
            subHeader={taskListName}
            open={true}
        >
            {task.description && (
                <div className="body-large">
                    <div className="DocumentViewModal__description-title title-medium">{intl.get("taskDescriptionLabel").d("Task description:")}</div>
                    <div className="DocumentViewModal__description-body">
                        {task.description}
                    </div>
                </div>
            )}
            {task.images && task.images.map && (
                <ImageGalleryPreview
                    className={'DocumentViewModal_images'}
                    urls={task.images.map(imageMeta => ({
                        path: imageMeta?.path,
                        thumbPath: imageMeta?.thumbPath,
                    }))}
                />
            )}
            {task.relatedPage && (
                <div className="DocumentViewModal_relatedPage">
                    <div>{intl.get("relatedPageLabel").d('Related Page:')}</div>
                    <Button
                        style={"soft"}
                        type={"neutral"}
                        text={intl.get('item_context.show_in_workspace').d('Show in Tree')}
                        size={'sm'}
                        onClick={() => {
                            showInWorkspace(rootStore, task.relatedPage!, rootStore.view.workspace!);
                            rootStore.view.openPageByName(PAGES.DOC_EDIT, {id: task.relatedPage!})
                        }}
                        Icon={JumpToElementIcon}
                    />
                </div>
            )}
            <h2 className="DocumentViewModal_header">{itemHeader}</h2>
            <div className="ER-position-group DocumentViewModal_page">
                <PreventRerender innerRef={editorNodeRef} className="ER-editor-wrapper"/>
            </div>
        </SideModal>
    );
});
